import queryKeys from '@/constants/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useToast from '../useToast';
import {
  publicSubmitContentCreatorApplicationPost,
  submitContentCreatorApplicationPost,
  TSubmitContentCreatorApplicationBody,
  TVerifyContentCreatorApplicationBody,
} from '@/services/api/requests/contentCreatorApplications';
import { TCustomAxiosError } from '@/services/api';
import { useAuthContext } from '@/context/auth/useAuthContext';

type TSubmitContentCreatorApplicationParams = {
  applicationBody?: TSubmitContentCreatorApplicationBody;
  isExistingUser: boolean;
  publicCredentials?: TVerifyContentCreatorApplicationBody;
};

const useSubmitContentCreatorApplication = (onSuccessCallback?: () => void) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { refreshUserData } = useAuthContext();

  const { isPending, mutate } = useMutation<unknown, TCustomAxiosError | Error, TSubmitContentCreatorApplicationParams>(
    {
      mutationFn: async ({
        applicationBody,
        isExistingUser,
        publicCredentials,
      }: TSubmitContentCreatorApplicationParams) => {
        if (!applicationBody) {
          throw new Error('Missing content creator application data.');
        }

        if (isExistingUser) {
          return submitContentCreatorApplicationPost(applicationBody);
        }

        if (!publicCredentials?.email || !publicCredentials.token) {
          throw new Error('Missing content creator application credentials.');
        }

        return publicSubmitContentCreatorApplicationPost({ publicCredentials, body: applicationBody });
      },
      onSuccess: async (_response, variables) => {
        if (variables.isExistingUser) {
          await refreshUserData();
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: [queryKeys.contentCreatorApplications.me] }),
            queryClient.invalidateQueries({ queryKey: [queryKeys.content.author] }),
          ]);
        }

        onSuccessCallback?.();
      },
      onError: (error: TCustomAxiosError | Error) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : error?.errors?.[0]?.message || 'An error occurred while submitting your application.';

        toast({
          status: 'error',
          title: 'Error',
          description: errorMessage,
        });
      },
    },
  );

  return { isPending, mutate };
};

export default useSubmitContentCreatorApplication;
